import type { ReactNode } from 'react';
import tw from 'tailwind/tailwind-theme-gentux';

import TrackedLink from 'components/sanity/TrackedLink';

type ProductButtonProps = {
  btnText: string;
  elementLabel?: string;
  href: string;
  imgUrl: string;
  imgAlt: string;
  trackerClass?: string;
};

type PanelWithFloatBtnProps = ProductButtonProps & {};
export type TwoOrThreeItems<T> = [T, T] | [T, T, T];

type BuySuitOrTuxSectionProps = {
  items: TwoOrThreeItems<ProductButtonProps>;
  primaryCta: string | ReactNode;
  secondaryCta: string | ReactNode;
  subtitle: string;
  title: string;
} & ClassNameProp;

const PanelWithFloatBtn = ({ imgUrl, imgAlt, btnText, elementLabel, href, trackerClass }: PanelWithFloatBtnProps) => (
  <TrackedLink
    href={href}
    className={`margin-auto relative flex h-auto w-5/6 flex-col xs:w-[300px] sm:items-start ${trackerClass ?? ''}`}
    elementLabel={elementLabel}
    reloadDocument
  >
    <img loading="lazy" src={imgUrl} alt={imgAlt} />
    <button className="btn-info btn absolute bottom-32 left-0 right-0 mx-16">{btnText}</button>
  </TrackedLink>
);

const BuySuitOrTuxSection = ({ className, title, subtitle, primaryCta, secondaryCta, items }: BuySuitOrTuxSectionProps) => {
  return (
    <div className={`m-auto mb-16 max-w-[2000px] pb-32 md:mb-16 md:pb-0 ${className ?? ''}`}>
      <div className="grid grid-flow-row md:grid-flow-col">
        <div className="pl-16 md:pl-32 md:pt-32">
          <div className="relative row-span-2">
            <h3 className="text-h3 mt-24 text-gold-dark">{title.toUpperCase()}</h3>
            <div className="absolute -top-0 -left-16 z-10 bg-gold-dark pl-16 pr-32 text-white md:-left-32 md:pl-32 md:pr-64">
              {subtitle}
            </div>
          </div>
          <div className="mt-64">
            {primaryCta}

            <picture>
              <source key="md-up" media={`(min-width: ${tw.screens.md})`} srcSet={`https://gentux.imgix.net/2x_jackets.png?auto=format`} />
              <source key="base" srcSet={`https://gentux.imgix.net/jackets.png?auto=format`} />
              <img className="w-full" alt="A stack of suit jackets" />
            </picture>
          </div>
        </div>

        <div className="flex flex-col justify-center">
          <div
            className={`grid grid-cols-2 gap-y-16 px-0 py-16 xs:gap-x-16 xs:px-32 sm:grid-rows-[auto_auto] md:items-start md:justify-between md:py-0 md:px-16 ${
              items.length === 3 ? 'sm:grid-cols-3' : ''
            }`}
          >
            {items.map((item, i) => (
              <div
                key={`retail-item-${i}`}
                className={`col-span-1 flex flex-row items-center justify-center ${
                  items.length === 2 && i === 0
                    ? 'xs:justify-end md:justify-start'
                    : items.length === 2 && i === 1
                      ? 'xs:justify-start md:justify-center'
                      : ''
                }`}
              >
                <PanelWithFloatBtn
                  imgUrl={item.imgUrl}
                  imgAlt={item.imgAlt}
                  btnText={item.btnText.toUpperCase()}
                  elementLabel={item.elementLabel}
                  href={item.href}
                  trackerClass={item.trackerClass}
                />
              </div>
            ))}

            <div
              className={`flex flex-col items-center justify-center sm:col-span-3 ${
                items.length === 2 ? 'col-span-2 row-start-2' : 'col-span-1 sm:row-start-2'
              } sm:mt-32`}
            >
              {secondaryCta}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BuySuitOrTuxSection;
